import { BREAKPOINTS } from '@app/constants/breakpoints';
import { DEFAULT_ITEMS_COUNT_PER_REQUEST } from '@app/constants/limits';
import { useAmplitude } from '@app/utils/analytics/useAmplitude';
import { cn } from '@app/utils/cn';
import { useScreenWidth } from '@app/utils/hooks';
import { isDefined } from '@app/utils/is';
import * as Sentry from '@sentry/react';
import { Table as ReactTable } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';

import { Button } from '../Button';
import { Dropdown, DropdownMenu, DropdownTrigger } from '../Dropdown';
import { ChevronLeft, ChevronRight } from '../Icons';

import { Pager } from './components/Pager';
import { PagerDots } from './components/PagerDots';
import { usePaginationRange } from './usePaginationRange';

type SetPageIndexType = ReactTable<unknown>['setPageIndex'];

interface Props {
  manualPagination?: boolean;
  onChange: SetPageIndexType;
  current?: number;
  pageSize?: number;
  totalItems?: number | undefined;
  className?: string;
  actionButtonLabel?: string;
  onActionButtonClick?: () => void;
  isHidden?: boolean;
}

export const Pagination = ({
  manualPagination = true,
  onChange,
  current = 1,
  totalItems = 0,
  pageSize = DEFAULT_ITEMS_COUNT_PER_REQUEST,
  className,
  actionButtonLabel,
  onActionButtonClick,
  isHidden,
}: Props) => {
  const { t } = useTranslation(['pagination', 'common']);

  const currentScreenWidth = useScreenWidth();
  const isBreakpointXS = currentScreenWidth < BREAKPOINTS.XS;
  const isBreakpointSM = currentScreenWidth < BREAKPOINTS.SM;

  const currentPage = Math.max(current, 1);
  const totalPageCount = Math.ceil(totalItems / pageSize);

  const isArrowVisible = totalPageCount > 1;

  const paginationRange = usePaginationRange({
    currentPage,
    isBreakpointSM,
    totalPageCount,
  });

  const lastItemIndex = paginationRange.length - 1;
  const lastPagerCount = paginationRange[lastItemIndex]?.page ?? 1;

  const dropDownItems = paginationRange.map(item => ({
    key: item.page.toString(),
    label: item.page.toString(),
  }));

  const isPrevEnabled = current > 1;
  const isNextEnabled = current < totalPageCount;
  const { trackEvent } = useAmplitude();

  const handlePrevArrowClick = (page: number) => {
    handleChange(page);
    Sentry.setTag('pagination_used', 'prev');
    trackEvent({ name: 'pagination.button.prev.click' });
  };

  const handleNextArrowClick = (page: number) => {
    handleChange(page);
    Sentry.setTag('pagination_used', 'next');
    trackEvent({ name: 'pagination.button.next.click' });
  };

  const handleClick = (type: 'dots' | 'page') => (page: number) => {
    handleChange(page);
    Sentry.setTag('pagination_used', type);
    trackEvent({ name: `pagination.button.${type}.click` });
  };

  if (isHidden) {
    return null;
  }

  const handleChange = (newPage: number) => {
    onChange(manualPagination ? newPage : newPage - 1);
  };

  return (
    <div className="sticky bottom-0 left-0 z-pagination mt-auto flex w-full items-center bg-neutral-96 py-2 sm:relative lg:py-3 lg:pb-6">
      <ul className={cn('flex h-10 flex-1 list-none items-center sm:justify-center', className)}>
        {isArrowVisible && (
          <Pager
            ariaLabel={t('pagination:previous_page')}
            onClick={isPrevEnabled ? () => handlePrevArrowClick(currentPage - 1) : undefined}
            disabled={!isPrevEnabled}
          >
            <ChevronLeft
              className={cn('text-neutral-40', {
                'text-neutral-80': !isPrevEnabled,
              })}
            />
          </Pager>
        )}

        {isBreakpointSM && (
          <>
            <Dropdown title={t('common:page_one')}>
              <DropdownTrigger>
                <Pager ariaLabel={t('pagination:go_to_page', { number: currentPage })} isActive onClick={() => handleChange(currentPage)}>
                  {currentPage}
                </Pager>
              </DropdownTrigger>
              <DropdownMenu
                selectionMode="single"
                selectedKeys={[currentPage.toString()]}
                items={dropDownItems}
                onSelectionChange={([newPage]) => handleChange(parseInt(newPage ?? '1'))}
              />
            </Dropdown>

            {!isBreakpointXS && (
              <>
                <span className="inline-block p-2 body-1">/</span>
                <Pager ariaLabel={t('pagination:go_to_page', { number: lastPagerCount })} onClick={() => handleChange(lastPagerCount)}>
                  {paginationRange[lastItemIndex]?.page}
                </Pager>
              </>
            )}
          </>
        )}

        {!isBreakpointSM &&
          paginationRange.map(({ dotsType = null, page }, index) => {
            return (
              <Pager
                key={index}
                ariaLabel={t('pagination:go_to_page', { number: page })}
                isActive={page === currentPage}
                onClick={() => handleClick(isDefined(dotsType) ? 'dots' : 'page')(page)}
              >
                {!isDefined(dotsType) ? page : <PagerDots shouldRotate={dotsType === 'right'} />}
              </Pager>
            );
          })}

        {isArrowVisible && (
          <Pager
            ariaLabel={t('pagination:next_page')}
            disabled={!isNextEnabled}
            onClick={isNextEnabled ? () => handleNextArrowClick(currentPage + 1) : undefined}
          >
            <ChevronRight
              className={cn('text-neutral-40', {
                'text-neutral-80': !isNextEnabled,
              })}
            />
          </Pager>
        )}
      </ul>

      {isBreakpointSM && isDefined(onActionButtonClick) && isDefined(actionButtonLabel) && (
        <Button onClick={onActionButtonClick} dataTestId="createSelectionButton">
          {actionButtonLabel}
        </Button>
      )}
    </div>
  );
};
